/**
 * The pinyin initials in their chart order, one row per place of articulation (lips, tongue tip,
 * back of the tongue, palate, retroflex, dental). The semivowels `y` and `w` close the chart: the
 * spelling starts syllables with them even though phonologists count them as part of the final.
 */
export const PINYIN_INITIAL_ROWS = [
  ["b", "p", "m", "f"],
  ["d", "t", "n", "l"],
  ["g", "k", "h"],
  ["j", "q", "x"],
  ["zh", "ch", "sh", "r"],
  ["z", "c", "s"],
  ["y", "w"],
] as const;

export type PinyinInitial = (typeof PINYIN_INITIAL_ROWS)[number][number];

export const PINYIN_INITIALS: readonly PinyinInitial[] = PINYIN_INITIAL_ROWS.flat();

const PINYIN_INITIAL_SET: ReadonlySet<string> = new Set(PINYIN_INITIALS);

function isPinyinInitial(value: string): value is PinyinInitial {
  return PINYIN_INITIAL_SET.has(value);
}

/**
 * The initial a tone-number syllable is spelled with (`zhi4` → `zh`, `yi1` → `y`, `nv3` → `n`), or
 * `null` for a syllable with no initial (`an4`, `er4`, `e4`).
 */
export function getPinyinInitial(toneNumbersPinyin: string): PinyinInitial | null {
  const syllable = toneNumbersPinyin.trim().toLowerCase();
  // Two letters first, so `zhi4` is spelled with `zh` rather than `z`.
  for (const candidate of [syllable.slice(0, 2), syllable.slice(0, 1)]) {
    if (isPinyinInitial(candidate)) {
      return candidate;
    }
  }
  return null;
}
